import React, { useState } from "react";
import { useLogger } from "../../services/logging";
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Card,
  CardContent,
  CardActions,
  Divider,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Delete,
  Edit,
  Save,
  Cancel,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useTemplates, useTemplateCategories } from "../../hooks/useScenarios";
import { useAuth } from "../../contexts/AuthContext";
import {
  CreateScenarioDto,
  ScenarioCategory,
  RiskLevel,
  TimeFrame,
  RuleType,
  ExitType,
  EntryRuleInput,
  ExitRuleInput,
  RiskManagementInput,
  ScenarioTemplate,
} from "../../types/scenario";

interface RuleFormData {
  name: string;
  description: string;
  type: string;
  parameters: Record<string, any>;
  priority: number;
  isActive: boolean;
  condition: string;
  value?: number;
}

const steps = [
  "Basic Information",
  "Entry Rules",
  "Exit Rules",
  "Risk Management",
  "Review",
];

export function ScenarioForm() {
  const logger = useLogger("ScenarioForm");
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data state
  const [formData, setFormData] = useState<CreateScenarioDto>({
    name: "",
    description: "",
    category: ScenarioCategory.DAY_TRADING,
    riskLevel: RiskLevel.MODERATE,
    timeFrame: TimeFrame.INTRADAY,
    userId: user?.id || "",
    isTemplate: false,
    entryRules: [],
    exitRules: [],
    riskManagement: {
      maxPositionSize: 1000,
      maxOpenPositions: 5,
      maxDailyLoss: 500,
      maxWeeklyLoss: 2000,
      maxDrawdown: 10,
      stopLossPercentage: 5,
      positionSizingMethod: "FIXED_AMOUNT",
      maxCorrelation: 0.7,
      maxSectorExposure: 25,
      volatilityAdjustment: false,
    },
  });

  // Entry and exit rules state
  const [entryRules, setEntryRules] = useState<RuleFormData[]>([]);
  const [exitRules, setExitRules] = useState<RuleFormData[]>([]);

  // Template selection state
  const [selectedTemplate, setSelectedTemplate] =
    useState<ScenarioTemplate | null>(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);

  // Fetch templates and categories
  const { templates, loading: templatesLoading } = useTemplates();
  const { categories, loading: categoriesLoading } = useTemplateCategories();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      name: "",
      description: "",
      category: ScenarioCategory.DAY_TRADING,
      riskLevel: RiskLevel.MODERATE,
      timeFrame: TimeFrame.INTRADAY,
      userId: user?.id || "",
      isTemplate: false,
      entryRules: [],
      exitRules: [],
      riskManagement: {
        maxPositionSize: 1000,
        maxOpenPositions: 5,
        maxDailyLoss: 500,
        maxWeeklyLoss: 2000,
        maxDrawdown: 10,
        stopLossPercentage: 5,
        positionSizingMethod: "FIXED_AMOUNT",
        maxCorrelation: 0.7,
        maxSectorExposure: 25,
        volatilityAdjustment: false,
      },
    });
    setEntryRules([]);
    setExitRules([]);
  };

  const handleInputChange = (field: keyof CreateScenarioDto, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRiskManagementChange = (
    field: keyof RiskManagementInput,
    value: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      riskManagement: {
        ...prev.riskManagement!,
        [field]: value,
      },
    }));
  };

  const addEntryRule = () => {
    const newRule: RuleFormData = {
      name: "",
      description: "",
      type: RuleType.TECHNICAL_INDICATOR,
      parameters: {},
      priority: entryRules.length + 1,
      isActive: true,
      condition: "AND",
    };
    setEntryRules((prev) => [...prev, newRule]);
  };

  const updateEntryRule = (
    index: number,
    field: keyof RuleFormData,
    value: any,
  ) => {
    setEntryRules((prev) =>
      prev.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule)),
    );
  };

  const removeEntryRule = (index: number) => {
    setEntryRules((prev) => prev.filter((_, i) => i !== index));
  };

  const addExitRule = () => {
    const newRule: RuleFormData = {
      name: "",
      description: "",
      type: ExitType.STOP_LOSS,
      parameters: {},
      priority: exitRules.length + 1,
      isActive: true,
      condition: "OR",
      value: 0,
    };
    setExitRules((prev) => [...prev, newRule]);
  };

  const updateExitRule = (
    index: number,
    field: keyof RuleFormData,
    value: any,
  ) => {
    setExitRules((prev) =>
      prev.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule)),
    );
  };

  const removeExitRule = (index: number) => {
    setExitRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTemplateSelect = (template: ScenarioTemplate) => {
    setSelectedTemplate(template);
    setFormData((prev) => ({
      ...prev,
      name: template.name,
      description: template.description,
      category: template.category,
      riskLevel: template.riskLevel,
      timeFrame: template.timeFrame,
      templateId: template.id,
    }));

    if (template.entryRules) {
      setEntryRules(
        template.entryRules.map((rule) => ({
          name: rule.name,
          description: rule.description,
          type: rule.type,
          parameters: rule.parameters,
          priority: rule.priority,
          isActive: rule.isActive,
          condition: rule.condition,
        })),
      );
    }

    if (template.exitRules) {
      setExitRules(
        template.exitRules.map((rule) => ({
          name: rule.name,
          description: rule.description,
          type: rule.type,
          parameters: rule.parameters,
          priority: rule.priority,
          isActive: rule.isActive,
          condition: rule.condition,
          value: rule.value,
        })),
      );
    }

    if (template.riskManagement) {
      setFormData((prev) => ({
        ...prev,
        riskManagement: template.riskManagement,
      }));
    }

    setShowTemplateDialog(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Convert form data to API format
      const scenarioData: CreateScenarioDto = {
        ...formData,
        entryRules: entryRules.map((rule) => ({
          name: rule.name,
          description: rule.description,
          type: rule.type as RuleType,
          parameters: rule.parameters,
          priority: rule.priority,
          isActive: rule.isActive,
          condition: rule.condition,
        })),
        exitRules: exitRules.map((rule) => ({
          name: rule.name,
          description: rule.description,
          type: rule.type as ExitType,
          parameters: rule.parameters,
          priority: rule.priority,
          isActive: rule.isActive,
          condition: rule.condition,
          value: rule.value,
        })),
      };

      // TODO: Call API service to create scenario
      logger.debug(
        "Creating scenario",
        { method: "handleSubmit" },
        { scenarioData },
      );

      // Navigate back to scenarios page
      navigate("/scenarios");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create scenario",
      );
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return (
          formData.name.trim() !== "" && formData.description.trim() !== ""
        );
      case 1:
        return (
          entryRules.length > 0 &&
          entryRules.every((rule) => rule.name.trim() !== "")
        );
      case 2:
        return (
          exitRules.length > 0 &&
          exitRules.every((rule) => rule.name.trim() !== "")
        );
      case 3:
        return formData.riskManagement !== undefined;
      default:
        return true;
    }
  };

  const renderBasicInformation = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Scenario Details
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Scenario Name"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            value={formData.category}
            label="Category"
            onChange={(e) => handleInputChange("category", e.target.value)}
          >
            {Object.values(ScenarioCategory).map((category) => (
              <MenuItem key={category} value={category}>
                {category.replace("_", " ")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Description"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Risk Level</InputLabel>
          <Select
            value={formData.riskLevel}
            label="Risk Level"
            onChange={(e) => handleInputChange("riskLevel", e.target.value)}
          >
            {Object.values(RiskLevel).map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Time Frame</InputLabel>
          <Select
            value={formData.timeFrame}
            label="Time Frame"
            onChange={(e) => handleInputChange("timeFrame", e.target.value)}
          >
            {Object.values(TimeFrame).map((frame) => (
              <MenuItem key={frame} value={frame}>
                {frame}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.isTemplate}
              onChange={(e) =>
                handleInputChange("isTemplate", e.target.checked)
              }
            />
          }
          label="Save as Template"
        />
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowTemplateDialog(true)}
        >
          Use Template
        </Button>
      </Grid>
    </Grid>
  );

  const renderEntryRules = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Entry Rules</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addEntryRule}
          >
            Add Rule
          </Button>
        </Box>
      </Grid>

      {entryRules.map((rule, index) => (
        <Grid item xs={12} key={index}>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Rule Name"
                    value={rule.name}
                    onChange={(e) =>
                      updateEntryRule(index, "name", e.target.value)
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={rule.type}
                      label="Type"
                      onChange={(e) =>
                        updateEntryRule(index, "type", e.target.value)
                      }
                    >
                      {Object.values(RuleType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.replace("_", " ")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={rule.description}
                    onChange={(e) =>
                      updateEntryRule(index, "description", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Priority"
                    value={rule.priority}
                    onChange={(e) =>
                      updateEntryRule(
                        index,
                        "priority",
                        parseInt(e.target.value),
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Condition</InputLabel>
                    <Select
                      value={rule.condition}
                      label="Condition"
                      onChange={(e) =>
                        updateEntryRule(index, "condition", e.target.value)
                      }
                    >
                      <MenuItem value="AND">AND</MenuItem>
                      <MenuItem value="OR">OR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={rule.isActive}
                        onChange={(e) =>
                          updateEntryRule(index, "isActive", e.target.checked)
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <IconButton color="error" onClick={() => removeEntryRule(index)}>
                <Delete />
              </IconButton>
            </CardActions>
          </Card>
        </Grid>
      ))}

      {entryRules.length === 0 && (
        <Grid item xs={12}>
          <Box textAlign="center" p={3}>
            <Typography variant="body1" color="text.secondary">
              No entry rules defined. Add at least one entry rule to continue.
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderExitRules = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Exit Rules</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addExitRule}
          >
            Add Rule
          </Button>
        </Box>
      </Grid>

      {exitRules.map((rule, index) => (
        <Grid item xs={12} key={index}>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Rule Name"
                    value={rule.name}
                    onChange={(e) =>
                      updateExitRule(index, "name", e.target.value)
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={rule.type}
                      label="Type"
                      onChange={(e) =>
                        updateExitRule(index, "type", e.target.value)
                      }
                    >
                      {Object.values(ExitType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.replace("_", " ")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={rule.description}
                    onChange={(e) =>
                      updateExitRule(index, "description", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Priority"
                    value={rule.priority}
                    onChange={(e) =>
                      updateExitRule(
                        index,
                        "priority",
                        parseInt(e.target.value),
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Condition</InputLabel>
                    <Select
                      value={rule.condition}
                      label="Condition"
                      onChange={(e) =>
                        updateExitRule(index, "condition", e.target.value)
                      }
                    >
                      <MenuItem value="AND">AND</MenuItem>
                      <MenuItem value="OR">OR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={rule.isActive}
                        onChange={(e) =>
                          updateExitRule(index, "isActive", e.target.checked)
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
                {rule.type === ExitType.STOP_LOSS && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Stop Loss %"
                      value={rule.value || ""}
                      onChange={(e) =>
                        updateExitRule(
                          index,
                          "value",
                          parseFloat(e.target.value),
                        )
                      }
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
            <CardActions>
              <IconButton color="error" onClick={() => removeExitRule(index)}>
                <Delete />
              </IconButton>
            </CardActions>
          </Card>
        </Grid>
      ))}

      {exitRules.length === 0 && (
        <Grid item xs={12}>
          <Box textAlign="center" p={3}>
            <Typography variant="body1" color="text.secondary">
              No exit rules defined. Add at least one exit rule to continue.
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderRiskManagement = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Risk Management Settings
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Max Position Size ($)"
          value={formData.riskManagement?.maxPositionSize || ""}
          onChange={(e) =>
            handleRiskManagementChange(
              "maxPositionSize",
              parseFloat(e.target.value),
            )
          }
          inputProps={{ min: 0, step: 100 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Max Open Positions"
          value={formData.riskManagement?.maxOpenPositions || ""}
          onChange={(e) =>
            handleRiskManagementChange(
              "maxOpenPositions",
              parseInt(e.target.value),
            )
          }
          inputProps={{ min: 1, max: 20 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Max Daily Loss ($)"
          value={formData.riskManagement?.maxDailyLoss || ""}
          onChange={(e) =>
            handleRiskManagementChange(
              "maxDailyLoss",
              parseFloat(e.target.value),
            )
          }
          inputProps={{ min: 0, step: 100 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Max Weekly Loss ($)"
          value={formData.riskManagement?.maxWeeklyLoss || ""}
          onChange={(e) =>
            handleRiskManagementChange(
              "maxWeeklyLoss",
              parseFloat(e.target.value),
            )
          }
          inputProps={{ min: 0, step: 100 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Max Drawdown (%)"
          value={formData.riskManagement?.maxDrawdown || ""}
          onChange={(e) =>
            handleRiskManagementChange(
              "maxDrawdown",
              parseFloat(e.target.value),
            )
          }
          inputProps={{ min: 0, max: 100, step: 0.1 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Stop Loss (%)"
          value={formData.riskManagement?.stopLossPercentage || ""}
          onChange={(e) =>
            handleRiskManagementChange(
              "stopLossPercentage",
              parseFloat(e.target.value),
            )
          }
          inputProps={{ min: 0, max: 100, step: 0.1 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Position Sizing Method</InputLabel>
          <Select
            value={formData.riskManagement?.positionSizingMethod || ""}
            label="Position Sizing Method"
            onChange={(e) =>
              handleRiskManagementChange("positionSizingMethod", e.target.value)
            }
          >
            <MenuItem value="FIXED_AMOUNT">Fixed Amount</MenuItem>
            <MenuItem value="PERCENTAGE_OF_CAPITAL">
              Percentage of Capital
            </MenuItem>
            <MenuItem value="KELLY_CRITERION">Kelly Criterion</MenuItem>
            <MenuItem value="VOLATILITY_ADJUSTED">Volatility Adjusted</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Max Correlation"
          value={formData.riskManagement?.maxCorrelation || ""}
          onChange={(e) =>
            handleRiskManagementChange(
              "maxCorrelation",
              parseFloat(e.target.value),
            )
          }
          inputProps={{ min: 0, max: 1, step: 0.1 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Max Sector Exposure (%)"
          value={formData.riskManagement?.maxSectorExposure || ""}
          onChange={(e) =>
            handleRiskManagementChange(
              "maxSectorExposure",
              parseFloat(e.target.value),
            )
          }
          inputProps={{ min: 0, max: 100, step: 0.1 }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.riskManagement?.volatilityAdjustment || false}
              onChange={(e) =>
                handleRiskManagementChange(
                  "volatilityAdjustment",
                  e.target.checked,
                )
              }
            />
          }
          label="Enable Volatility Adjustment"
        />
      </Grid>
    </Grid>
  );

  const renderReview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Review Your Scenario
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Typography>
              <strong>Name:</strong> {formData.name}
            </Typography>
            <Typography>
              <strong>Category:</strong> {formData.category.replace("_", " ")}
            </Typography>
            <Typography>
              <strong>Risk Level:</strong> {formData.riskLevel}
            </Typography>
            <Typography>
              <strong>Time Frame:</strong> {formData.timeFrame}
            </Typography>
            <Typography>
              <strong>Description:</strong> {formData.description}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Risk Management
            </Typography>
            <Typography>
              <strong>Max Position Size:</strong> $
              {formData.riskManagement?.maxPositionSize?.toLocaleString()}
            </Typography>
            <Typography>
              <strong>Max Open Positions:</strong>{" "}
              {formData.riskManagement?.maxOpenPositions}
            </Typography>
            <Typography>
              <strong>Max Daily Loss:</strong> $
              {formData.riskManagement?.maxDailyLoss?.toLocaleString()}
            </Typography>
            <Typography>
              <strong>Stop Loss:</strong>{" "}
              {formData.riskManagement?.stopLossPercentage}%
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Entry Rules ({entryRules.length})
            </Typography>
            {entryRules.map((rule, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  {index + 1}. {rule.name} - {rule.type.replace("_", " ")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rule.description}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Exit Rules ({exitRules.length})
            </Typography>
            {exitRules.map((rule, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  {index + 1}. {rule.name} - {rule.type.replace("_", " ")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rule.description}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderBasicInformation();
      case 1:
        return renderEntryRules();
      case 2:
        return renderExitRules();
      case 3:
        return renderRiskManagement();
      case 4:
        return renderReview();
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create Trading Scenario
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Build a comprehensive trading strategy with entry/exit rules and risk
          management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>

          <Box>
            <Button variant="outlined" onClick={handleReset} sx={{ mr: 1 }}>
              Reset
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              >
                {loading ? "Creating..." : "Create Scenario"}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceed()}
                endIcon={<ArrowForward />}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Template Selection Dialog */}
      <Dialog
        open={showTemplateDialog}
        onClose={() => setShowTemplateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Template</DialogTitle>
        <DialogContent>
          {templatesLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {templates.map((template) => (
                <Grid item xs={12} sm={6} key={template.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {template.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {template.description}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                          label={template.category.replace("_", " ")}
                          size="small"
                          color="primary"
                        />
                        <Chip
                          label={template.riskLevel}
                          size="small"
                          color="secondary"
                        />
                        <Chip
                          label={template.timeFrame}
                          size="small"
                          color="info"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
